import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Globe, Loader2, Quote } from 'lucide-react';
import { testimonialsFallback } from '../data/testimonialsFallback';
import { getTestimonialBySlug } from '../services/testimonialsService';
import { useLanguage } from '../contexts/LanguageContext';

export const TestimonialDetail = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const fallback = useMemo(
    () => testimonialsFallback.find((item) => item.slug === slug) || null,
    [slug]
  );
  const [testimonial, setTestimonial] = useState(fallback);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTestimonialBySlug(slug, language)
      .then((data) => setTestimonial(data || fallback))
      .catch(() => setTestimonial(fallback))
      .finally(() => setLoading(false));
  }, [fallback, slug, language]);

  useEffect(() => {
    if (!testimonial) return;
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content');

    document.title = testimonial.seoTitle || `${testimonial.name} | Frankly Talking English`;
    if (meta) {
      meta.setAttribute('content', testimonial.seoDescription || testimonial.excerpt || '');
    }

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) {
        meta.setAttribute('content', previousDescription || '');
      }
    };
  }, [testimonial]);

  if (!loading && !testimonial) {
    return <Navigate to="/testimonials" replace />;
  }

  // F8: slugs with no local fallback rendered a blank page while the API call was
  // in flight — show an explicit loading state instead of flashing blank.
  if (!testimonial) {
    return (
      <main className="pt-24 bg-cream-gradient min-h-screen flex items-center justify-center" data-testid="testimonial-detail-page">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </main>
    );
  }

  const paragraphs = String(testimonial.content || '')
    .split('\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const image = testimonial.imageUrl || testimonial.image || testimonial.avatar;
  const alt = testimonial.imageAlt || testimonial.name;

  return (
    <main className="pt-24 bg-cream-gradient min-h-screen" data-testid="testimonial-detail-page">
      <article className="section-padding">
        <div className="container-custom max-w-5xl">
          <Link
            to="/testimonials"
            className="inline-flex items-center gap-2 text-[#1A2E6E]/70 hover:text-[#D72B2B] font-medium mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> {t('testimonialDetail.backLink')}
          </Link>

          <motion.header
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <span className="badge-orange mb-4 inline-block">{t('testimonialDetail.badge')}</span>
            <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-4">
              {testimonial.name}
            </h1>
            {testimonial.title ? (
              <p className="text-lg text-[#1A2E6E]/70">{testimonial.title}</p>
            ) : null}
            {testimonial.location ? (
              <p className="text-sm text-[#4CAF50] flex items-center justify-center gap-1 mt-2">
                <Globe size={14} /> {testimonial.location}
              </p>
            ) : null}
          </motion.header>

          {image ? (
            <motion.img
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              src={image}
              alt={alt}
              className="w-full max-h-[520px] object-contain bg-[#F5F7FA] rounded-3xl shadow-xl mb-10"
            />
          ) : null}

          <motion.blockquote
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="glass-card rounded-3xl p-8 md:p-10 mb-10 text-center"
          >
            <Quote size={40} className="text-[#F5A623] mx-auto mb-4" />
            <p className="font-sora text-2xl md:text-3xl text-[#1A2E6E] font-semibold leading-snug">
              {testimonial.shortQuote}
            </p>
          </motion.blockquote>

          <div className="glass-card rounded-3xl p-8 md:p-12 mb-12">
            <div className="prose prose-lg max-w-none">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-[#1A2E6E]/80 leading-8 mb-6 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <footer className="glass-card rounded-3xl p-8 md:p-10 text-center bg-white">
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {t('testimonialDetail.ctaTitle')}
            </h2>
            <p className="text-[#1A2E6E]/70 mb-8 max-w-2xl mx-auto">
              {t('testimonialDetail.ctaBody')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                {t('testimonialDetail.ctaPrimary')} <ArrowRight size={18} />
              </Link>
              <Link to="/book-trial" className="btn-secondary">
                {t('testimonialDetail.ctaSecondary')}
              </Link>
            </div>
          </footer>
        </div>
      </article>
    </main>
  );
};

export default TestimonialDetail;
